import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  dismissAnnouncement,
  listAnnouncementInbox,
  listVisibleAnnouncements,
  markAnnouncementRead,
  type Announcement,
  type AnnouncementInboxItem,
} from 'shared/data/repositories/announcementRepository';
import { useAppSelector } from 'hooks/useAppStore';

export const announcementKeys = {
  visible: ['announcements', 'visible'] as const,
  inbox: ['announcements', 'inbox'] as const,
};

export function useVisibleAnnouncements() {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  return useQuery<Announcement[]>({
    queryKey: announcementKeys.visible,
    queryFn: () => listVisibleAnnouncements(5),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
}

export function useAnnouncementInbox(enabled = true) {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  return useQuery<AnnouncementInboxItem[]>({
    queryKey: announcementKeys.inbox,
    queryFn: () => listAnnouncementInbox(20),
    enabled: isAuthenticated && enabled,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useMarkAnnouncementRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markAnnouncementRead(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: announcementKeys.inbox });
      const previous = queryClient.getQueryData<AnnouncementInboxItem[]>(announcementKeys.inbox);
      queryClient.setQueryData<AnnouncementInboxItem[]>(
        announcementKeys.inbox,
        (curr) =>
          (curr ?? []).map((item) => (item.id === id ? { ...item, isRead: true } : item))
      );
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(announcementKeys.inbox, ctx.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: announcementKeys.inbox });
    },
  });
}

export function useDismissAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dismissAnnouncement(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: announcementKeys.visible });
      const previous = queryClient.getQueryData<Announcement[]>(announcementKeys.visible);
      queryClient.setQueryData<Announcement[]>(
        announcementKeys.visible,
        (curr: Announcement[] | undefined) =>
          (curr ?? []).filter((item: Announcement) => item.id !== id)
      );
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(announcementKeys.visible, ctx.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: announcementKeys.visible });
      // Banner dismiss must not remove inbox rows - refresh inbox so UI stays consistent.
      void queryClient.invalidateQueries({ queryKey: announcementKeys.inbox });
    },
  });
}
